import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import swal from "sweetalert";
import Header from "../../../../components/Header";
import useAuth from "../../../../auth/useAuth";
import { useFetchGET } from "../../../../state/FetchHelpers";
import {
  getSalesByCompanyReportRoute,
  reseiverCountriesRoute,
  salesFiltersRoute,
} from "../../../../config";
import { useCompanies } from "../../../hooks/companies/useCompanies.tsx";
import Loading from "../../../global/Loading";
import FatalError from "../../../global/FatalError";
import SalesKpiCards from "./SalesKpiCards";
import SalesCharts from "./SalesCharts";
import SalesTable from "./SalesTable";
import SalesRowDetailDialog from "./SalesRowDetailDialog";

const formatDate = (date) =>
  new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);

const getTodayRange = () => {
  const today = new Date();
  return { init_date: formatDate(today), end_date: formatDate(today) };
};

const getThisWeekRange = () => {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - diffToMonday);
  return { init_date: formatDate(monday), end_date: formatDate(today) };
};

const getThisMonthRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return { init_date: formatDate(firstDay), end_date: formatDate(today) };
};

const ReporteVentas = () => {
  const auth = useAuth();
  const token = auth.globals.token;
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const { companyListData, companyListLoading, companyListError } = useCompanies();
  const { data: countryListData, loading: countryListLoading, error: countryListError } = useFetchGET(
    reseiverCountriesRoute(), token, []
  );
  const { data: filtersData, loading: filtersLoading, error: filtersError } = useFetchGET(
    salesFiltersRoute(), token, null
  );

  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const mostrarErrorRequest = (error) => {
    if (error != null) {
      swal({ title: "Error", text: "Resultado: " + error, icon: "error", button: "Aceptar" });
      if (error === "No tiene autorización o su sesión ha expirado") auth.logout();
    }
  };

  const fetchSalesReport = async (values) => {
    if (!values.init_date || !values.end_date) return;
    setReportData(null);

    try {
      setLoadingReport(true);
      const res = await fetch(getSalesByCompanyReportRoute(values), { headers: authHeaders });
      if (res.ok) {
        const result = await res.json();
        setReportData(Array.isArray(result) ? result : []);
      } else if (res.status === 401 || res.status === 403) {
        mostrarErrorRequest("No tiene autorización o su sesión ha expirado");
      } else {
        const errData = await res.json().catch(() => null);
        mostrarErrorRequest(errData?.detail || errData?.message || "No fue posible obtener el reporte");
      }
    } catch (e) {
      mostrarErrorRequest(e.message || "Error de conexión");
    } finally {
      setLoadingReport(false);
    }

  };

  const today = formatDate(new Date());

  const reportFormik = useFormik({
    initialValues: {
      init_date: today,
      end_date: today,
      companies: [],
      countries: [],
      states: [],
      cities: [],
      group_by: "day",
    },
    onSubmit: (values) => fetchSalesReport(values),
  });

  const applyPresetRange = (range) => {
    const newValues = { ...reportFormik.values, ...range };
    reportFormik.setValues(newValues);
    fetchSalesReport(newValues);
  };

  const availableStates = useMemo(() => filtersData?.states ?? [], [filtersData]);

  const availableCities = useMemo(() => {
    const cities = filtersData?.cities ?? [];
    if (reportFormik.values.states.length === 0) return cities;
    return cities.filter((c) => reportFormik.values.states.includes(c.state));
  }, [filtersData, reportFormik.values.states]);

  const handleStatesChange = (newStates) => {
    reportFormik.setFieldValue("states", newStates);
    if (newStates.length > 0 && reportFormik.values.cities.length > 0) {
      const validCityNames = (filtersData?.cities ?? [])
        .filter((c) => newStates.includes(c.state))
        .map((c) => c.city);
      reportFormik.setFieldValue(
        "cities",
        reportFormik.values.cities.filter((c) => validCityNames.includes(c))
      );
    }
  };

  const totals = useMemo(() => {
    return (reportData || []).reduce(
      (acc, row) => {
        acc.facturado += Number(row.total_facturado) || 0;
        acc.cobrado += Number(row.total_cobrado) || 0;
        acc.pendiente += Number(row.pendiente) || 0;
        acc.paquetes += (Number(row.pickup_count) || 0) + (Number(row.delivery_count) || 0);
        return acc;
      },
      { facturado: 0, cobrado: 0, pendiente: 0, paquetes: 0 }
    );
  }, [reportData]);

  if (companyListLoading || countryListLoading || filtersLoading) return <Loading />;
  if (companyListError || countryListError || filtersError) return <FatalError />;

  const gridSx = { "& > div": { gridColumn: isNonMobile ? undefined : "span 12" } };

  return (
    <Box m="20px" marginBottom="64px">
      <Header title="Reporte de Ventas" subtitle="Facturación y cobranza por empresa / país" />

      <form onSubmit={reportFormik.handleSubmit}>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Button size="small" variant="outlined" color="inherit" onClick={() => applyPresetRange(getTodayRange())}>Hoy</Button>
          <Button size="small" variant="outlined" color="inherit" onClick={() => applyPresetRange(getThisWeekRange())}>Esta semana</Button>
          <Button size="small" variant="outlined" color="inherit" onClick={() => applyPresetRange(getThisMonthRange())}>Este mes</Button>
        </Box>

        {/* Fila 1: Fecha inicial | Fecha final | Empresas | Países */}
        <Box
          display="grid"
          gap="15px"
          marginBottom="15px"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={gridSx}
        >
          <TextField
            label="Fecha inicial"
            name="init_date"
            type="date"
            value={reportFormik.values.init_date}
            onChange={reportFormik.handleChange}
            variant="filled"
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ gridColumn: "span 3" }}
            required
          />
          <TextField
            label="Fecha final"
            name="end_date"
            type="date"
            value={reportFormik.values.end_date}
            onChange={reportFormik.handleChange}
            variant="filled"
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ gridColumn: "span 3" }}
            required
          />

          <FormControl variant="filled" sx={{ gridColumn: "span 3" }}>
            <InputLabel id="select-companies-label">Empresas</InputLabel>
            <Select
              labelId="select-companies-label"
              multiple
              value={reportFormik.values.companies}
              onChange={(e) => reportFormik.setFieldValue("companies", e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Todas"
                  : companyListData.filter((c) => selected.includes(c.id)).map((c) => c.name).join(", ")
              }
              size="small"
            >
              {companyListData.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  <Checkbox checked={reportFormik.values.companies.includes(company.id)} />
                  <ListItemText primary={company.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="filled" sx={{ gridColumn: "span 3" }}>
            <InputLabel id="select-countries-label">Países</InputLabel>
            <Select
              labelId="select-countries-label"
              multiple
              value={reportFormik.values.countries}
              onChange={(e) => reportFormik.setFieldValue("countries", e.target.value)}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Todos"
                  : countryListData.filter((c) => selected.includes(c.id)).map((c) => c.name).join(", ")
              }
              size="small"
            >
              {countryListData.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  <Checkbox checked={reportFormik.values.countries.includes(country.id)} />
                  <ListItemText primary={country.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Fila 2: Estados | Ciudades | Agrupar por */}
        <Box
          display="grid"
          gap="15px"
          marginBottom="15px"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={gridSx}
        >
          <FormControl variant="filled" sx={{ gridColumn: "span 4" }}>
            <InputLabel id="select-states-label">Estados</InputLabel>
            <Select
              labelId="select-states-label"
              multiple
              value={reportFormik.values.states}
              onChange={(e) => handleStatesChange(e.target.value)}
              renderValue={(selected) => selected.length === 0 ? "Todos" : selected.join(", ")}
              size="small"
            >
              {availableStates.map((state) => (
                <MenuItem key={state} value={state}>
                  <Checkbox checked={reportFormik.values.states.includes(state)} />
                  <ListItemText primary={state} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="filled" sx={{ gridColumn: "span 4" }}>
            <InputLabel id="select-cities-label">Ciudades</InputLabel>
            <Select
              labelId="select-cities-label"
              multiple
              value={reportFormik.values.cities}
              onChange={(e) => reportFormik.setFieldValue("cities", e.target.value)}
              renderValue={(selected) => selected.length === 0 ? "Todas" : selected.join(", ")}
              size="small"
            >
              {availableCities.map((c) => (
                <MenuItem key={`${c.state}-${c.city}`} value={c.city}>
                  <Checkbox checked={reportFormik.values.cities.includes(c.city)} />
                  <ListItemText primary={c.city} secondary={c.state} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="filled" sx={{ gridColumn: "span 4" }}>
            <InputLabel id="select-groupby-label">Agrupar por</InputLabel>
            <Select
              labelId="select-groupby-label"
              value={reportFormik.values.group_by}
              onChange={reportFormik.handleChange}
              name="group_by"
              size="small"
            >
              <MenuItem value="day">Día</MenuItem>
              <MenuItem value="week">Semana</MenuItem>
              <MenuItem value="month">Mes</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button type="submit" variant="contained" color="secondary" disabled={loadingReport}>
            {loadingReport ? <CircularProgress size={20} color="inherit" /> : "Buscar"}
          </Button>
        </Box>
      </form>

      {reportData === null ? (
        <Typography align="center" mt={4} color="text.secondary">
          Selecciona un rango de fechas y presiona "Buscar" para generar el reporte.
        </Typography>
      ) : reportData.length === 0 ? (
        <Typography align="center" mt={4} color="text.secondary">
          No se encontraron registros para los filtros seleccionados.
        </Typography>
      ) : (
        <>
          <SalesKpiCards totals={totals} />
          <SalesCharts data={reportData} />
          <SalesTable data={reportData} onRowClick={setSelectedRow} />
        </>
      )}

      <SalesRowDetailDialog
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        row={selectedRow}
      />

    </Box>
  );
};

export default ReporteVentas;
